'use client';

import { useState, useEffect } from 'react';

interface PoiMarina {
  id: string;
  name: string;
  city: string | null;
  zone: string | null;
}

interface Props {
  /** Zona corrente da filters.zone. */
  zone: string | null;
  /** Città corrente da filters.port (city). */
  city: string | null;
  /** Porto FK corrente da filters.port_id. */
  port_id: string | null;
  onZoneChange: (zone: string | null) => void;
  onCityChange: (city: string | null) => void;
  onPortChange: (port_id: string | null, label: string | null) => void;
}

const SELECT_CLS =
  'w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

export default function CatalogLocationCascade({
  zone,
  city,
  port_id,
  onZoneChange,
  onCityChange,
  onPortChange,
}: Props) {
  const [pois, setPois] = useState<PoiMarina[]>([]);

  useEffect(() => {
    fetch('/api/pois?category=marina')
      .then((r) => r.json())
      .then((data) => setPois(data.data || []))
      .catch(() => {});
  }, []);

  const zones = [
    ...new Set(pois.map((p) => p.zone).filter((z): z is string => !!z)),
  ].sort((a, b) => a.localeCompare(b, 'it'));

  const citiesForZone = [
    ...new Set(
      pois
        .filter((p) => !zone || p.zone === zone)
        .map((p) => p.city)
        .filter((c): c is string => !!c),
    ),
  ].sort((a, b) => a.localeCompare(b, 'it'));

  const portsForCity = pois
    .filter((p) => (!zone || p.zone === zone) && (!city || p.city === city))
    .sort((a, b) => a.name.localeCompare(b.name, 'it'));

  return (
    <div className="space-y-3" data-cid="catalog-location-cascade">
      {/* Zona */}
      <div data-cid="catalog-location-zone-wrap">
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          Zona / Località
        </label>
        <select
          value={zone || ''}
          onChange={(e) => onZoneChange(e.target.value || null)}
          className={SELECT_CLS}
          data-cid="catalog-location-zone-select"
        >
          <option value="">Tutte le zone</option>
          {zones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      </div>

      {/* Città */}
      <div data-cid="catalog-location-city-wrap">
        <label className="block text-xs font-semibold text-slate-500 mb-1.5">
          Città
        </label>
        <select
          value={city || ''}
          onChange={(e) => onCityChange(e.target.value || null)}
          className={SELECT_CLS}
          data-cid="catalog-location-city-select"
        >
          <option value="">Tutte le città</option>
          {citiesForZone.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {/* Porto — visibile solo se ci sono porti per la selezione corrente */}
      {portsForCity.length > 0 && (
        <div data-cid="catalog-location-port-wrap">
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">
            Porto / Marina
          </label>
          <select
            value={port_id || ''}
            onChange={(e) => {
              const selected = pois.find((p) => p.id === e.target.value);
              onPortChange(e.target.value || null, selected?.name || null);
            }}
            className={SELECT_CLS}
            data-cid="catalog-location-port-select"
          >
            <option value="">Tutti i porti</option>
            {portsForCity.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
